import React from 'react';
import { FAQRows } from '@charitystack/shared';
import useFAQs from '../hooks/useFAQs';
import PaneSheet, { usePaneSheet } from './PaneSheet';
import DetailDescription, { DETAIL_LINK_CLASS } from './DetailDescription';
import { FAQPaneDrawer } from './FAQInlinePanel';

/**
 * The detail page's one "learn more" link, above its primary button (owner call 2026-10-21):
 * - the description is clipped -> "Learn more" opens a sheet over the card with the full
 *   description and, below it, the FAQs;
 * - the description fits -> "FAQs" opens the FAQs sheet (when there are any);
 * - neither -> nothing.
 * The card never grows, so it always ends on its primary button. The card must be
 * `position: relative`, clip its overflow and carry `data-pane-sheet-scope`. EmbedFormDetailPage
 * and EventDetailPage both render this.
 */
export default function DetailLearnMore({ title, descriptionHtml, isDescriptionClipped, faqs: propFaqs, elementID }) {
  const { faqs: hookFaqs } = useFAQs(propFaqs ? null : elementID);
  const faqs = propFaqs || hookFaqs;
  const hasFaqs = Array.isArray(faqs) && faqs.length > 0;
  const sheet = usePaneSheet({ inline: true });

  if (!isDescriptionClipped) {
    return hasFaqs ? <FAQPaneDrawer faqs={faqs} trigger="inline" /> : null;
  }

  return (
    <>
      <button
        ref={sheet.triggerRef}
        type="button"
        onClick={sheet.open}
        aria-expanded={sheet.isOpen}
        aria-haspopup="dialog"
        className={DETAIL_LINK_CLASS}
      >
        Learn more
      </button>
      {sheet.isOpen && (
        <PaneSheet title={title || 'About'} closeLabel="Close" onClose={sheet.close} container={sheet.container}>
          <DetailDescription html={descriptionHtml} className="pt-[var(--cs-space-5)]" />
          {hasFaqs && (
            <section className="mt-[var(--cs-space-6)]" aria-label="Frequently asked questions">
              <h3 className="m-0 font-sans text-[length:var(--cs-text-base)] leading-[var(--cs-leading-24)] font-semibold text-[color:var(--cs-text-primary)]">
                Frequently asked questions
              </h3>
              <FAQRows faqs={faqs} size="compact" />
            </section>
          )}
        </PaneSheet>
      )}
    </>
  );
}
